import { useCaseModuleModalRootActionsStore } from '@/features/cases/_shared/components/case-modal-root/case-modal-root.store';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  DeleteLabelCaseValidationSchema,
  TDeleteLabelCaseValidation,
  useDeleteLabelCaseMutation,
} from '../../../api/delete-label-case.api';
import { zodResolver } from '@hookform/resolvers/zod';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_LABEL_CASE_QUERY_KEY } from '../../../api/get-list-label-case.api';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
  ModalCloseButton,
} from '@/components/_ui/modal';
import { handleDomBubbleEvent } from '@/utils/dom';
import { Spinner } from '@/components/_ui/spinner';

export type TDeleteLabelCaseModalType = 'DeleteLabelCaseModal';
export interface IDeleteLabelCaseModalProps {
  labelCaseId: string;
}
const DeleteLabelCaseModal = memo(({ labelCaseId }: IDeleteLabelCaseModalProps) => {
  const caseModuleModalRootActions = useCaseModuleModalRootActionsStore();
  const showToast = useCustomToast();

  const isOpenDeleteLabelCaseModal = useMemo(() => {
    return caseModuleModalRootActions.checkIsOpenModal('DeleteLabelCaseModal');
  }, [caseModuleModalRootActions]);

  const form = useForm<TDeleteLabelCaseValidation>({
    resolver: zodResolver(DeleteLabelCaseValidationSchema),
    mode: 'onChange',
    defaultValues: {
      labelCaseId: labelCaseId || '',
    },
  });

  useEffect(() => {
    if (labelCaseId) {
      form.reset({
        labelCaseId: labelCaseId || '',
      });
    }
  }, [labelCaseId, form]);

  const mutationDeleteLabelCase = useDeleteLabelCaseMutation({
    mutationConfigOptions: {
      onError: (_error) => {
        const errorMessage = axiosExtractErrorMessage(_error) || 'Error delete label case, please try again';
        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: () => {
        Promise.all([queryClient.invalidateQueries({ queryKey: [GET_LIST_LABEL_CASE_QUERY_KEY] })]);
        caseModuleModalRootActions.closeModal('DeleteLabelCaseModal');
        showToast({
          message: 'Label case deleted successfully!',
          type: 'success',
        });
      },
    },
  });

  const handleSubmitDeleteLabelCase = useCallback(
    (data: TDeleteLabelCaseValidation) => {
      mutationDeleteLabelCase.mutate({
        labelCaseId: data?.labelCaseId || '',
      });
    },
    [mutationDeleteLabelCase]
  );

  const handleClose = useCallback(() => {
    caseModuleModalRootActions.closeModal('DeleteLabelCaseModal');
  }, [caseModuleModalRootActions]);

  return (
    <Modal isOpen={isOpenDeleteLabelCaseModal} lockBodyScroll onClose={handleClose} className="p-[32px] w-[500px]">
      <ModalHeader>
        <ModalTitle>Delete Label Case</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(handleSubmitDeleteLabelCase)(e);
          }}
          id="delete-label-case-form"
          className="grid gap-[16px]"
        >
          <span className="text-[14px] font-normal text-[#9D9D9C]">Do you want to delete this label case?</span>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton form="delete-label-case-form" type="submit" disabled={mutationDeleteLabelCase.isPending}>
          {mutationDeleteLabelCase.isPending ? <Spinner isLoading={true} className="w-[18px] h-[18px]" /> : null} Delete
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});
DeleteLabelCaseModal.displayName = 'DeleteLabelCaseModal';

export default DeleteLabelCaseModal;
